import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import '../DnsPropagationCheck/DnsPropagationCheck.css';
import {
  DnsPropagationCheckNowButton,
  DnsPropagationScoreBar,
  DnsWaitRecommendation,
  DnsReadyToSendBanner,
  DnsLastCheckedFooter
} from '../dnsPropagationShared';

function formatPropagationData(data) {
  return {
    domain: data.domain,
    readyToSend: data.readyToSend || false,
    propagationScore: data.overallPropagationScore || data.propagationScore || 0,
    recommendation: {
      status: data.overallStatus || data.summary?.status || 'NOT_READY',
      message: data.summary?.message || data.message || 'Status unknown',
      canSendEmails: data.readyToSend || false,
      waitTime: data.recommendedWaitTime || 0
    }
  };
}

const DnsPropagationCheckSimple = ({ domain, onStatusChange }) => {
  const [propagationStatus, setPropagationStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const initialLoadRef = useRef(true);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const runPropagationCheck = useCallback(async () => {
    setIsChecking(true);
    try {
      const response = await api.axios.post('/api/domains/check-readiness', { domain });
      const formattedData = formatPropagationData(response.data);

      setPropagationStatus(formattedData);
      setLastCheckTime(new Date());
      setInitialLoad(false);
      initialLoadRef.current = false;

      onStatusChangeRef.current?.(formattedData);
    } catch (error) {
      console.error('Error checking propagation:', error);
      const errorStatus = {
        domain,
        readyToSend: false,
        propagationScore: 0,
        recommendation: {
          status: 'ERROR',
          message: 'Failed to check DNS propagation. Please try again.',
          canSendEmails: false,
          waitTime: 5
        }
      };
      setPropagationStatus(errorStatus);
      setInitialLoad(false);
      initialLoadRef.current = false;
    } finally {
      setIsChecking(false);
    }
  }, [domain]);

  const fetchPropagationStatus = useCallback(async () => {
    try {
      const response = await api.axios.get(`/api/domains/${domain}/propagation`);

      if (response.data && response.data.lastPropagationCheck) {
        const formattedData = formatPropagationData(response.data);
        setPropagationStatus(formattedData);
        setLastCheckTime(new Date(response.data.lastPropagationCheck));
        setInitialLoad(false);
        initialLoadRef.current = false;

        onStatusChangeRef.current?.(formattedData);
      } else {
        if (initialLoadRef.current) {
          await runPropagationCheck();
        }
        initialLoadRef.current = false;
        setInitialLoad(false);
      }
    } catch (error) {
      console.error('Error fetching propagation status:', error);
      if (initialLoadRef.current) {
        await runPropagationCheck();
      }
      initialLoadRef.current = false;
      setInitialLoad(false);
    }
  }, [domain, runPropagationCheck]);

  useEffect(() => {
    initialLoadRef.current = true;
    if (domain) {
      fetchPropagationStatus();
    }
  }, [domain, fetchPropagationStatus]);

  if (initialLoad && !propagationStatus) {
    return (
      <div className="dns-propagation-check loading">
        <div className="propagation-header">
          <h4>DNS Propagation Status</h4>
          <DnsPropagationCheckNowButton onClick={runPropagationCheck} isChecking={isChecking} />
        </div>
        <p>
          Click &quot;Check Now&quot; to verify DNS propagation status across 8 worldwide DNS servers.
          <br />
          <small style={{ color: '#666' }}>
            This check ensures your domain is ready for email sending and helps prevent delivery issues.
          </small>
        </p>
      </div>
    );
  }

  if (propagationStatus && propagationStatus.propagationScore >= 100 && propagationStatus.readyToSend) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        backgroundColor: '#e8f5e8',
        borderRadius: 1,
        border: '1px solid #4caf50'
      }}
      >
        <CheckCircleIcon sx={{ color: '#4caf50' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
          DNS Propagation Status
        </Typography>
        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
          🎉 Ready to Send!
        </Typography>
      </Box>
    );
  }

  return (
    <div className="dns-propagation-check">
      <div className="propagation-header">
        <h4>DNS Propagation Status</h4>
        <DnsPropagationCheckNowButton onClick={runPropagationCheck} isChecking={isChecking} />
      </div>

      {propagationStatus && (
        <>
          <div className="propagation-summary">
            <DnsPropagationScoreBar
              readyToSend={propagationStatus.readyToSend}
              score={propagationStatus.propagationScore}
            />
          </div>

          {!propagationStatus.readyToSend && propagationStatus.recommendation?.waitTime > 0 && (
            <DnsWaitRecommendation
              waitMinutes={propagationStatus.recommendation.waitTime}
            />
          )}

          {propagationStatus.readyToSend && <DnsReadyToSendBanner />}
        </>
      )}

      <DnsLastCheckedFooter
        lastCheckTime={lastCheckTime}
        timeSinceLastCheck={propagationStatus?.timeSinceLastCheck}
      />
    </div>
  );
};

export default DnsPropagationCheckSimple;
